export type EmbeddedBrowserStatus = {
    supported: true
    protocolVersion: 1 | 2
    partition: string
    features: {
        webview: true
        capturePage: true
        domActions: true
        clearData: true
        popupRouting: true
        cookieImport: boolean
        profileRotation: boolean
        certificateExceptions: boolean
    }
}

export type EmbeddedBrowserCookieProfile = { id: string; name: string }

export type EmbeddedBrowserCookieImportError = "invalid_cookie" | "cookie_write_failed" | "source_warning"

export type EmbeddedBrowserCookieImportResult = {
    requested: number
    imported: number
    skipped: number
    warnings: number
    partial: boolean
    firstErrors: EmbeddedBrowserCookieImportError[]
    partition: string
    retiredPartitionCleared: boolean
}

export type EmbeddedBrowserShortcut =
    | { type: "new-tab" | "close-tab" | "focus-address" | "reload" | "hard-reload" | "next-tab" | "previous-tab" | "go-back" | "go-forward" }
    | { type: "select-tab"; index: number }

export type EmbeddedBrowserCertificateError = {
    challengeId: string
    guestId: number
    url: string
    host: string
    error: string
    subjectName?: string
    issuerName?: string
    validStart?: number
    validExpiry?: number
}

export type EmbeddedBrowserEvent =
    | { type: "open-tab"; guestId: number; url: string }
    | { type: "download-blocked"; guestId: number }
    | { type: "navigation-blocked"; guestId: number }
    | { type: "certificate-error"; warning: EmbeddedBrowserCertificateError }
    | { type: "profile-cleared" }
    | { type: "partition-rotated"; partition: string }
    | { type: "shortcut"; guestId: number; command: EmbeddedBrowserShortcut }

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function guestId(payload: unknown): number | null {
    if (!isRecord(payload) || typeof payload.guestId !== "number" || !Number.isInteger(payload.guestId)) return null
    return payload.guestId
}

function parseStatus(value: unknown): EmbeddedBrowserStatus | null {
    if (
        !isRecord(value) ||
        value.supported !== true ||
        (value.protocolVersion !== 1 && value.protocolVersion !== 2) ||
        typeof value.partition !== "string" ||
        !isRecord(value.features)
    ) {
        return null
    }
    const features = value.features
    if (
        features.webview !== true ||
        features.capturePage !== true ||
        features.domActions !== true ||
        features.clearData !== true ||
        features.popupRouting !== true
    ) {
        return null
    }
    return {
        supported: true,
        protocolVersion: value.protocolVersion,
        partition: value.partition,
        features: {
            webview: true,
            capturePage: true,
            domActions: true,
            clearData: true,
            popupRouting: true,
            cookieImport: value.protocolVersion === 2 && features.cookieImport === true && features.profileRotation === true,
            profileRotation: value.protocolVersion === 2 && features.profileRotation === true,
            certificateExceptions: value.protocolVersion === 2 && features.certificateExceptions === true,
        },
    }
}

function optionalBoundedString(value: unknown): string | undefined | null {
    if (value === undefined) return undefined
    if (typeof value !== "string" || value.length === 0 || value.length > 200) return null
    return value
}

function optionalTimestamp(value: unknown): number | undefined | null {
    if (value === undefined) return undefined
    if (typeof value !== "number" || !Number.isFinite(value) || value < 0) return null
    return value
}

function parseCertificateError(payload: unknown): EmbeddedBrowserCertificateError | null {
    const id = guestId(payload)
    if (
        id === null ||
        !isRecord(payload) ||
        typeof payload.challengeId !== "string" ||
        payload.challengeId.length === 0 ||
        payload.challengeId.length > 100 ||
        typeof payload.url !== "string" ||
        payload.url.length === 0 ||
        payload.url.length > 8_192 ||
        typeof payload.error !== "string" ||
        payload.error.length === 0 ||
        payload.error.length > 200
    )
        return null
    let parsedUrl: URL
    try {
        parsedUrl = new URL(payload.url)
    } catch {
        return null
    }
    if (parsedUrl.protocol !== "https:" || parsedUrl.username.length > 0 || parsedUrl.password.length > 0) return null
    const subjectName = optionalBoundedString(payload.subjectName)
    const issuerName = optionalBoundedString(payload.issuerName)
    const validStart = optionalTimestamp(payload.validStart)
    const validExpiry = optionalTimestamp(payload.validExpiry)
    if (subjectName === null || issuerName === null || validStart === null || validExpiry === null) return null
    return {
        challengeId: payload.challengeId,
        guestId: id,
        url: parsedUrl.toString(),
        host: parsedUrl.hostname,
        error: payload.error,
        ...(subjectName ? { subjectName } : {}),
        ...(issuerName ? { issuerName } : {}),
        ...(validStart !== undefined ? { validStart } : {}),
        ...(validExpiry !== undefined ? { validExpiry } : {}),
    }
}

export function hasEmbeddedBrowser(): boolean {
    return typeof window !== "undefined" && window.wildlarkAPI?.browser !== undefined
}

export async function getEmbeddedBrowserStatus(): Promise<EmbeddedBrowserStatus | null> {
    const browser = window.wildlarkAPI?.browser
    if (!browser) return null
    try {
        return parseStatus(await browser.status())
    } catch {
        return null
    }
}

export async function clearEmbeddedBrowserData(): Promise<boolean> {
    const browser = window.wildlarkAPI?.browser
    if (!browser) return false
    const result = await browser.clearData()
    return isRecord(result) && result.cleared === true
}

export async function proceedEmbeddedBrowserCertificateError(challengeId: string): Promise<{ url: string } | null> {
    const browser = window.wildlarkAPI?.browser
    if (!browser?.proceedCertificateError) return null
    try {
        const result = await browser.proceedCertificateError(challengeId)
        if (!isRecord(result) || result.approved !== true || typeof result.url !== "string") return null
        const url = new URL(result.url)
        if (url.protocol !== "https:" || url.username.length > 0 || url.password.length > 0) return null
        return { url: url.toString() }
    } catch {
        return null
    }
}

export async function dismissEmbeddedBrowserCertificateError(challengeId: string): Promise<boolean> {
    const browser = window.wildlarkAPI?.browser
    if (!browser?.dismissCertificateError) return false
    try {
        const result = await browser.dismissCertificateError(challengeId)
        return isRecord(result) && result.dismissed === true
    } catch {
        return false
    }
}

export async function listEmbeddedBrowserCookieProfiles(): Promise<EmbeddedBrowserCookieProfile[] | null> {
    const browser = window.wildlarkAPI?.browser
    if (!browser?.listCookieProfiles) return null
    try {
        const result = await browser.listCookieProfiles()
        if (!isRecord(result) || !Array.isArray(result.profiles)) return null
        const profiles: EmbeddedBrowserCookieProfile[] = []
        for (const profile of result.profiles) {
            if (!isRecord(profile) || typeof profile.id !== "string" || typeof profile.name !== "string") return null
            profiles.push({ id: profile.id, name: profile.name })
        }
        return profiles
    } catch {
        return null
    }
}

function isEmbeddedBrowserCookieImportError(value: unknown): value is EmbeddedBrowserCookieImportError {
    return value === "invalid_cookie" || value === "cookie_write_failed" || value === "source_warning"
}

export async function importEmbeddedBrowserCookies(profileId: string): Promise<EmbeddedBrowserCookieImportResult | null> {
    const browser = window.wildlarkAPI?.browser
    if (!browser?.importCookies) return null
    try {
        const result = await browser.importCookies(profileId)
        if (
            !isRecord(result) ||
            typeof result.requested !== "number" ||
            !Number.isInteger(result.requested) ||
            result.requested < 0 ||
            typeof result.imported !== "number" ||
            !Number.isInteger(result.imported) ||
            result.imported < 0 ||
            typeof result.skipped !== "number" ||
            !Number.isInteger(result.skipped) ||
            result.skipped < 0 ||
            typeof result.warnings !== "number" ||
            !Number.isInteger(result.warnings) ||
            result.warnings < 0 ||
            typeof result.partial !== "boolean" ||
            typeof result.partition !== "string" ||
            result.partition.length === 0 ||
            typeof result.retiredPartitionCleared !== "boolean" ||
            !Array.isArray(result.firstErrors)
        ) {
            return null
        }
        const firstErrors: EmbeddedBrowserCookieImportError[] = []
        for (const error of result.firstErrors) {
            if (!isEmbeddedBrowserCookieImportError(error)) return null
            firstErrors.push(error)
        }
        if (result.imported + result.skipped !== result.requested || firstErrors.length > 3) return null
        return {
            requested: result.requested,
            imported: result.imported,
            skipped: result.skipped,
            warnings: result.warnings,
            partial: result.partial,
            firstErrors,
            partition: result.partition,
            retiredPartitionCleared: result.retiredPartitionCleared,
        }
    } catch {
        return null
    }
}

export function subscribeEmbeddedBrowserEvents(callback: (event: EmbeddedBrowserEvent) => void): () => void {
    const browser = window.wildlarkAPI?.browser
    if (!browser) return () => undefined
    const removers = [
        browser.onOpenTabRequested((payload) => {
            const id = guestId(payload)
            if (id !== null && isRecord(payload) && typeof payload.url === "string") callback({ type: "open-tab", guestId: id, url: payload.url })
        }),
        browser.onDownloadBlocked((payload) => {
            const id = guestId(payload)
            if (id !== null) callback({ type: "download-blocked", guestId: id })
        }),
        browser.onNavigationBlocked((payload) => {
            const id = guestId(payload)
            if (id !== null) callback({ type: "navigation-blocked", guestId: id })
        }),
        ...(browser.onCertificateError
            ? [
                  browser.onCertificateError((payload) => {
                      const warning = parseCertificateError(payload)
                      if (warning) callback({ type: "certificate-error", warning })
                  }),
              ]
            : []),
        browser.onProfileCleared(() => callback({ type: "profile-cleared" })),
        ...(browser.onPartitionRotated
            ? [
                  browser.onPartitionRotated((payload) => {
                      if (isRecord(payload) && typeof payload.partition === "string" && payload.partition.length > 0) {
                          callback({ type: "partition-rotated", partition: payload.partition })
                      }
                  }),
              ]
            : []),
        browser.onShortcut((payload) => {
            const id = guestId(payload)
            if (id === null || !isRecord(payload) || typeof payload.command !== "string") return
            if (payload.command === "select-tab") {
                if (typeof payload.tabIndex === "number" && Number.isInteger(payload.tabIndex) && payload.tabIndex >= 0 && payload.tabIndex <= 8) {
                    callback({ type: "shortcut", guestId: id, command: { type: "select-tab", index: payload.tabIndex } })
                }
                return
            }
            if (
                payload.command === "new-tab" ||
                payload.command === "close-tab" ||
                payload.command === "focus-address" ||
                payload.command === "reload" ||
                payload.command === "hard-reload" ||
                payload.command === "next-tab" ||
                payload.command === "previous-tab" ||
                payload.command === "go-back" ||
                payload.command === "go-forward"
            ) {
                callback({ type: "shortcut", guestId: id, command: { type: payload.command } })
            }
        }),
    ]
    return () => removers.forEach((remove) => remove())
}
